import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Container } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLoader } from "../../../context/LoaderContext";
import List from "./List";
import { fetchFeatures, createFeature, updateFeature } from "../../../redux/slice/subscriptionSlice";
import { useToast } from "../../../context/TostContext";

const Features = () => {
    const dispatch = useDispatch();
    const { setLoading } = useLoader();
    const toast = useToast();
    const [limit, setLimit] = useState({ value: 10, label: 10 });

    const headerPortalTarget = document.getElementById("header-portal-target");

    const headerContent = (
        <div className="d-flex align-items-center justify-content-between w-100">
            <h4 className="fw-semibold mb-0 text-primary fs-lg">
                <span className="text-muted fw-normal">Subscription / </span>Features
            </h4>
        </div>
    );

    const { features = [], isLoading, total } = useSelector(
        (state) => state.subscription || {}
    );

    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);

    const [openModal, setOpenModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchData = useCallback(() => {
        dispatch(
            fetchFeatures({
                search: debouncedSearchTerm,
                page: currentPage,
                limit: limit?.value,
            })
        );
    }, [dispatch, debouncedSearchTerm, currentPage, limit?.value]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (!debouncedSearchTerm && features?.length === 0) {
            setLoading(isLoading);
        } else {
            setLoading(false);
        }
    }, [isLoading, setLoading, features?.length, debouncedSearchTerm]);

    const handleSearchChange = (value) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const handleEdit = (item) => {
        setSelectedItem(item);
        setOpenModal(true);
    };

    const handleAdd = () => {
        setSelectedItem(null);
        setOpenModal(true);
    };


    const handlePageChange = (newPageNumber) => {
        setCurrentPage(newPageNumber);
    };

    const paginationConfig = useMemo(() => {
        const pageSize = limit?.value || 10;
        const totalPages = Math.ceil((total || 0) / pageSize);
        return {
            total: total || 0,
            totalPages,
            currentPage,
            setCurrentPage,
            pageSize,
            setPageSize: (size) => {
                setLimit({ value: size, label: size });
                setCurrentPage(1);
            },
            onPageChange: handlePageChange,
        };
    }, [total, currentPage, limit]);

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            let result;
            if (selectedItem) {
                result = await dispatch(updateFeature({ id: selectedItem.id, data: values })).unwrap();
            } else {
                result = await dispatch(createFeature(values)).unwrap();
            }

            if (result?.success) {
                toast.success(result.message || "Feature saved successfully!");
                setOpenModal(false);
                fetchData();
            }
        } catch (err) {
            toast.error(err?.message || "Operation failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            {headerPortalTarget && createPortal(headerContent, headerPortalTarget)}
            <Container fluid className="px-0">
                <div className="bg-white rounded-2 overflow-hidden common-shadow mt-4">
                    <List
                        data={features}
                        isLoading={isLoading}
                        searchTerm={searchTerm}
                        setSearchTerm={handleSearchChange}
                        setCurrentPage={setCurrentPage}
                        currentPage={currentPage}
                        handleEdit={handleEdit}
                        handleAdd={handleAdd}
                        refresh={fetchData}
                        limit={limit?.value}
                        showModal={openModal}
                        setShowModal={setOpenModal}
                        selectedItem={selectedItem}
                        onSubmit={handleSubmit}
                        pagination={paginationConfig}
                    />
                </div>
            </Container>
        </>
    );
};

export default Features;
